import React from 'react';
import { Box, Card, Typography, Chip, Button, Skeleton } from '@mui/material';
import {
  AutoFixHigh,
  Schedule,
  Security,
  Code,
  InsertDriveFile,
} from '@mui/icons-material';
import type { SimulatedImpact } from './simulatorUtils';

interface ImpactSummaryProps {
  impact: SimulatedImpact | null;
  onPreviewRemediation: () => void;
  canRemediate?: boolean;
  isLoading?: boolean;
}

export const ImpactSummary: React.FC<ImpactSummaryProps> = ({
  impact,
  onPreviewRemediation,
  canRemediate = true,
  isLoading = false,
}) => {
  const getImpactBadgeColor = (val: string) => {
    switch (val.toLowerCase()) {
      case 'high':
        return { color: '#b91c1c', bg: '#fef2f2', border: '#fca5a5' };
      case 'medium':
        return { color: '#c2410c', bg: '#fff7ed', border: '#fdba74' };
      case 'low':
        return { color: '#1d4ed8', bg: '#eff6ff', border: '#93c5fd' };
      default:
        return { color: '#64748b', bg: '#f8fafc', border: '#e2e8f0' };
    }
  };

  const secStyle = getImpactBadgeColor(impact?.securityImpact ?? 'Minimal');
  const debtStyle = getImpactBadgeColor(impact?.debtImpact ?? 'Low');

  return (
    <Card
      elevation={0}
      sx={{
        bgcolor: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '16px',
        boxShadow: '0 4px 16px -2px rgba(15, 23, 42, 0.05)',
        p: { xs: 2.5, sm: 3 },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 800,
            color: '#0f172a',
            fontSize: '0.92rem',
            letterSpacing: '-0.01em',
          }}
        >
          Fix Plan Summary
        </Typography>

        {isLoading || !impact ? (
          <Skeleton variant="rounded" width={110} height={24} sx={{ borderRadius: '12px', bgcolor: '#f1f5f9' }} />
        ) : (
          <Chip
            label={`Selected fixes: ${impact.selectedFixesCount}`}
            size="small"
            sx={{
              fontWeight: 700,
              fontSize: '0.74rem',
              bgcolor: impact.selectedFixesCount > 0 ? '#eff6ff' : '#f1f5f9',
              color: impact.selectedFixesCount > 0 ? '#2563eb' : '#64748b',
              border: '1px solid',
              borderColor: impact.selectedFixesCount > 0 ? '#bfdbfe' : '#e2e8f0',
            }}
          />
        )}
      </Box>

      {/* Details Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 2,
          mb: 3,
        }}
      >
        {/* Estimated effort */}
        <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: '10px', border: '1px solid #f1f5f9' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.5 }}>
            <Schedule sx={{ fontSize: 14, color: '#64748b' }} />
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem' }}>
              Estimated effort
            </Typography>
          </Box>
          {isLoading || !impact ? (
            <Skeleton width={60} height={26} />
          ) : (
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontWeight: 800,
                fontSize: '1.05rem',
                color: '#0f172a',
              }}
            >
              {impact.estimatedEffortFormatted}
            </Typography>
          )}
        </Box>

        {/* Files affected */}
        <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: '10px', border: '1px solid #f1f5f9' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.5 }}>
            <InsertDriveFile sx={{ fontSize: 14, color: '#64748b' }} />
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem' }}>
              Files affected
            </Typography>
          </Box>
          {isLoading || !impact ? (
            <Skeleton width={35} height={26} />
          ) : (
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontWeight: 800,
                fontSize: '1.05rem',
                color: '#0f172a',
              }}
            >
              {impact.filesAffectedCount}
            </Typography>
          )}
        </Box>

        {/* Security impact */}
        <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: '10px', border: '1px solid #f1f5f9' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.75 }}>
            <Security sx={{ fontSize: 14, color: '#64748b' }} />
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem' }}>
              Security impact
            </Typography>
          </Box>
          {isLoading || !impact ? (
            <Skeleton variant="rounded" width={55} height={20} sx={{ borderRadius: '10px' }} />
          ) : (
            <Chip
              label={impact.securityImpact}
              size="small"
              sx={{
                height: 20,
                fontWeight: 700,
                fontSize: '0.7rem',
                bgcolor: secStyle.bg,
                color: secStyle.color,
                border: `1px solid ${secStyle.border}`,
              }}
            />
          )}
        </Box>

        {/* Technical debt impact */}
        <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: '10px', border: '1px solid #f1f5f9' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.75 }}>
            <Code sx={{ fontSize: 14, color: '#64748b' }} />
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem' }}>
              Technical debt
            </Typography>
          </Box>
          {isLoading || !impact ? (
            <Skeleton variant="rounded" width={55} height={20} sx={{ borderRadius: '10px' }} />
          ) : (
            <Chip
              label={impact.debtImpact}
              size="small"
              sx={{
                height: 20,
                fontWeight: 700,
                fontSize: '0.7rem',
                bgcolor: debtStyle.bg,
                color: debtStyle.color,
                border: `1px solid ${debtStyle.border}`,
              }}
            />
          )}
        </Box>
      </Box>

      {/* Primary Remediation CTA */}
      <Button
        variant="contained"
        fullWidth
        disabled={isLoading || !canRemediate || !impact || impact.selectedFixesCount === 0}
        onClick={onPreviewRemediation}
        startIcon={<AutoFixHigh />}
        sx={{
          bgcolor: '#0f172a',
          color: '#ffffff',
          py: 1.25,
          borderRadius: '12px',
          textTransform: 'none',
          fontSize: '0.92rem',
          fontWeight: 700,
          boxShadow: '0 4px 12px rgba(15, 23, 42, 0.15)',
          transition: 'all 0.18s ease',
          '&:hover': {
            bgcolor: '#1e293b',
            boxShadow: '0 6px 16px rgba(15, 23, 42, 0.22)',
          },
          '&.Mui-disabled': {
            bgcolor: '#e2e8f0',
            color: '#94a3b8',
          },
        }}
      >
        Preview Remediation
      </Button>
    </Card>
  );
};
